import { useCallback } from 'react';

import { graphql, useMutation } from 'react-relay';

import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import { ConfluenceSpaceAri } from '@atlassian/ari/confluence/space';

import type { useCreateKbSourceLinkingMutationJsmCardMutation } from './__generated__/useCreateKbSourceLinkingMutationJsmCardMutation.graphql.ts';
import type { KbTypeOption } from './CreateKBCardForm';

type KbSourceVisibility = KbTypeOption['value'];
export const CREATE_KB_LINK_SOURCE_ALREADY_HAS_CONTENT_ERROR_CODE = 'KB_ALREADY_HAS_CONTENT';

type CreateKbLinkSourceError = Error & {
	code?: string;
};

const createLinkSourceAlreadyHasContentError = (): CreateKbLinkSourceError =>
	Object.assign(
		new Error(
			'Confluence space was created but failed to link with the knowledge base because it already has content',
		),
		{ code: CREATE_KB_LINK_SOURCE_ALREADY_HAS_CONTENT_ERROR_CODE },
	);

export const isCreateKbLinkSourceAlreadyHasContentError = (error: unknown): boolean =>
	Boolean(
		error &&
		typeof error === 'object' &&
		'code' in error &&
		(error as CreateKbLinkSourceError).code ===
			CREATE_KB_LINK_SOURCE_ALREADY_HAS_CONTENT_ERROR_CODE,
	);

const getSourceAri = ({ cloudId, spaceId }: { cloudId: string; spaceId: string }): string =>
	ConfluenceSpaceAri.create({
		siteId: cloudId,
		spaceId,
	}).toString();

const getSourceContainerAri = ({ cloudId }: { cloudId: string }): string =>
	ConfluenceSiteAri.create({
		siteId: cloudId,
	}).toString();

export const useCreateKbSourceLinking = (): {
	linkSpaceToKnowledgeBase: (params: {
		cloudId: string;
		projectIdentifier: string;
		spaceId: string;
		sourceVisibility: KbSourceVisibility;
		appLinkId?: string | null;
	}) => Promise<void>;
} => {
	const [commitLinkSources] = useMutation<useCreateKbSourceLinkingMutationJsmCardMutation>(graphql`
		mutation useCreateKbSourceLinkingMutationJsmCardMutation(
			$cloudId: ID!
			$projectIdentifier: String!
			$sources: [KnowledgeBaseSourceInput!]!
		) {
			knowledgeBase_linkSources(
				cloudId: $cloudId
				projectIdentifier: $projectIdentifier
				sources: $sources
			) {
				linkSourceResults {
					success
				}
			}
		}
	`);

	const linkSpaceToKnowledgeBase = useCallback(
		async ({
			cloudId,
			projectIdentifier,
			spaceId,
			sourceVisibility,
			appLinkId,
		}: {
			cloudId: string;
			projectIdentifier: string;
			spaceId: string;
			sourceVisibility: KbSourceVisibility;
			appLinkId?: string | null;
		}): Promise<void> =>
			new Promise((resolve, reject) => {
				commitLinkSources({
					variables: {
						cloudId,
						projectIdentifier,
						sources: [
							{
								sourceARI: getSourceAri({ cloudId, spaceId }),
								sourceContainerARI: getSourceContainerAri({ cloudId }),
								visibility: sourceVisibility,
								metadata: appLinkId ? { applicationIdentifier: appLinkId } : undefined,
							},
						],
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							reject(new Error(errors.map((error) => error.message).join(', ')));
							return;
						}

						const linkResult = response.knowledgeBase_linkSources?.linkSourceResults?.[0];
						if (!linkResult?.success) {
							reject(createLinkSourceAlreadyHasContentError());
							return;
						}

						resolve();
					},
					onError: (error) => reject(error),
				});
			}),
		[commitLinkSources],
	);

	return { linkSpaceToKnowledgeBase };
};
